import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import '../../../../../styles/PropertiesPage.css';
import MapView from './MapView';
import ListView from './ListView';
import { useFetch } from '../../../../../hooks/useFetch';
import { getPropertyInformations, Property } from '../../../../../api/Property';
import ProspectHeader from '../../../../common/ProspectHeader';
import LoadingOverlay from '../../../../common/LoadingOverlay';
import PropertyDetailView from './PropertyDetailView';
import { RouteComponentProps } from 'react-router-dom';
import NoPropertiesFoundModal from './NoPropertiesFoundModal';
import { prospectPages } from '../../ProspectRouter';
import PropertySearchbar from './PropertySearchbar';
import { parseQuery, PropertyQueryType, PropertyQuery, stringifyQuery } from './PropertyQuery';
import { getStateInformations } from '../../../../../api/LocationSearch';

export interface Props extends RouteComponentProps<{}> {}

const PropertiesPage = (props: Props) => {
    const backToSearch = () => {
        props.history.push(prospectPages.welcome.extension);
    };

    if (!props.location.search) {
        backToSearch();
        return null;
    }

    let query = useMemo(() => parseQuery(props.location.search), [props.location.search]);

    const [isMapView, setIsMapView] = useState(true);
    const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(undefined);
    const { data: properties, loading: propertiesLoading } = useFetch(
        getPropertyInformations,
        [],
        undefined
    );
    const { data: states, loading: statesLoading } = useFetch(getStateInformations, [], undefined);

    const setSearchQuery = (newQuery: PropertyQuery) => {
        props.history.push({ search: stringifyQuery(newQuery) });
    };

    const filterProperties = (allProperties: Property[]) => {
        if (query.type === PropertyQueryType.City && query.cityName) {
            let cityProperties = allProperties.filter(p => p.city === query.cityName);
            if (cityProperties.length === 0) {
                setSearchQuery({
                    type: PropertyQueryType.State,
                    stateAbbreviation: query.stateAbbreviation
                });
            }
            return cityProperties;
        }

        if (query.type === PropertyQueryType.Property && query.propertyName) {
            let property = allProperties.find(p => p.name === query.propertyName);
            if (!property) {
                return [];
            }

            return [property];
        }

        return allProperties.filter(p => p.state === query.stateAbbreviation);
    };

    const filteredProperties =
        properties && (properties.length === 0 ? properties : filterProperties(properties));
    useEffect(() => {
        if (
            filteredProperties &&
            filteredProperties.length === 1 &&
            query.type === PropertyQueryType.Property
        ) {
            setSelectedProperty(filteredProperties[0]);
        }
    }, [properties, query]);

    return (
        <div className="flex-root">
            <ProspectHeader />
            <div className="search-bar">
                <div onClick={() => setIsMapView(!isMapView)} className="text-nowrap view-toggle">
                    {isMapView ? 'List' : 'Map'} View
                </div>
                <PropertySearchbar valueSelected={setSearchQuery} />
            </div>
            <div className="properties-view">
                <LoadingOverlay
                    show={propertiesLoading || statesLoading}
                    inBootstrapColumn={false}
                />
                {isMapView ? (
                    <MapView
                        properties={filteredProperties || []}
                        states={states || []}
                        setSelectedProperty={setSelectedProperty}
                        selectedProperty={selectedProperty}
                        propertyQuery={query}
                    />
                ) : (
                    <ListView
                        properties={filteredProperties || []}
                        setSelectedProperty={setSelectedProperty}
                    />
                )}
            </div>
            <NoPropertiesFoundModal
                visible={!!filteredProperties && filteredProperties.length === 0}
                closeNoPropertiesFoundModal={backToSearch}
                clearSearch={backToSearch}
            />
            <PropertyDetailView
                property={selectedProperty}
                closeProperyDetail={() => setSelectedProperty(undefined)}
            />
        </div>
    );
};

export default PropertiesPage;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/public/properties/PropertiesPage.tsx